/**
 * Auth Store
 *
 * 백엔드 포트(services/backend)를 통한 인증 상태 관리
 * - 세션 초기화 및 복원 (새로고침 대응)
 * - 로그인/로그아웃
 * - 이메일 로그인 (관리자 승인 필요)
 */

import { create } from 'zustand';
import { getBackend } from '@/app/services/backend';
import type { AuthSession, AuthUser, UserProfile } from '@/app/services/backend';
import { useSessionStore } from './sessionStore';
import { attachOrCreateSession, closeSession } from '@/app/services/sm/simulationManagerService';
import { clearAttachmentUrlCache } from '@/app/services/issue/attachmentUrlCache';
import { runSignOutHooks } from './signOutHooks';
import {
  getStoredSessionId,
  setStoredSessionId,
  clearStoredSessionId,
} from '@/app/persistence/lastSession';
import toast from 'react-hot-toast';

export type { UserProfile } from '@/app/services/backend';

// StrictMode/중복 호출로 initialize가 동시에 실행될 때를 방지
let _initializePromise: Promise<void> | null = null;
// Auth 상태 리스너는 탭당 1개만 유지
let _authStateUnsubscribe: (() => void) | null = null;
let _lastSessionCreateAttemptAt = 0;
const SESSION_CREATE_RETRY_COOLDOWN_MS = 10000;

// 프로필 조회가 불가능할 때(RLS 거부·트리거 실패 등) 표시용 기본 프로필
function buildPendingProfile(user: AuthUser | null): UserProfile {
  return {
    id: user?.id || '',
    email: user?.email || '',
    display_name: user?.email?.split('@')[0] || null,
    approval_status: 'pending',
    is_admin: false,
    has_password: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

interface AuthState {
  // State
  user: AuthUser | null;
  session: AuthSession | null;
  profile: UserProfile | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, role?: 'engineer' | 'operator') => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  resetLoading: () => void;

  // Profile Actions
  fetchProfile: () => Promise<void>;

  // Profile Update Actions
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;

  // Admin Actions
  fetchAllUsers: () => Promise<UserProfile[]>;
  approveUser: (userId: string) => Promise<void>;
  rejectUser: (userId: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  session: null,
  profile: null,
  loading: true,
  initialized: false,
  error: null,

  /**
   * 앱 시작 시 세션 복원
   * 백엔드가 저장된 세션(localStorage 등)을 복원함
   */
  initialize: async () => {
    if (_initializePromise) {
      return _initializePromise;
    }

    _initializePromise = (async () => {
    // 이미 초기화되었으면 스킵
    if (get().initialized) {
      return;
    }

    try {
      set({ loading: true, error: null });

      const backend = await getBackend();

      // 현재 세션 가져오기 (저장소에서 자동 복원)
      let session: AuthSession | null = null;
      try {
        session = await backend.auth.getSession();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[AuthStore] Failed to get session:', message);
        set({ user: null, session: null, loading: false, initialized: true });
        return;
      }

      set({
        user: session?.user ?? null,
        session,
        loading: false,
        initialized: true,
      });

      console.log('[AuthStore] Session initialized:', session ? 'logged in' : 'not logged in');

      const hasSessionReady = () => {
        const { sessionId, sessionCreating } = useSessionStore.getState();
        return !!sessionId || sessionCreating;
      };

      const shouldThrottleSessionCreate = () => {
        const now = Date.now();
        const delta = now - _lastSessionCreateAttemptAt;
        return delta < SESSION_CREATE_RETRY_COOLDOWN_MS;
      };

      const ensureConnectSession = (source: string, userId: string | null | undefined) => {
        if (hasSessionReady()) {
          console.log('[AuthStore] Existing/creating SESSION_ID detected, skipping session creation:', source);
          return;
        }

        if (shouldThrottleSessionCreate()) {
          console.warn('[AuthStore] Session create throttled:', source);
          return;
        }

        _lastSessionCreateAttemptAt = Date.now();
        // 저장된 세션이 있으면 attach 우선 (#286) — 새로고침·순단 후 grace 내
        // 복귀면 실행 중 세션이 그대로 살아난다. 죽었으면 새 세션으로 폴백.
        const storedId = getStoredSessionId(userId);
        console.log('[AuthStore] Session event without SESSION_ID, ensuring Connect-RPC session:', source);
        attachOrCreateSession(storedId).then(sessionId => {
          useSessionStore.getState().setSessionId(sessionId);
          setStoredSessionId(userId, sessionId);
          console.log('[AuthStore] Connect-RPC session ready from:', source, sessionId);
        }).catch(err => {
          console.error('[AuthStore] Failed to establish Connect-RPC session from:', source, err);
          // 세션 최초 생성 실패 + (SM failover 재접속 window 소진) 최종 실패를 사용자에게 표면화.
          // 무음 자동 재접속이 끝내 실패하면 세션이 죽은 채 제어가 조용히 먹통이 되므로, 최소한 이 토스트로 알린다.
          toast.error('시뮬레이션 서버에 연결하지 못했습니다. 잠시 후 다시 시도하거나 페이지를 새로고침 해주세요.');
        });
      };

      // 로그인 상태이고 SESSION_ID가 없으면 Connect-RPC 세션 확보(attach 우선)
      if (session) {
        ensureConnectSession('init', session.user?.id);
      }

      // 인증 상태 변경 리스너 등록
      if (_authStateUnsubscribe) {
        _authStateUnsubscribe();
        _authStateUnsubscribe = null;
      }

      const subscription = backend.auth.onAuthStateChange((event, session) => {
        console.log('[AuthStore] Auth state changed:', event);

        // SIGNED_OUT에서 저장분을 지우려면 교체 전의 사용자가 필요하다
        const prevUserId = get().user?.id;

        set({
          user: session?.user ?? null,
          session,
        });

        // 세션 유효 이벤트 처리 - SESSION_ID가 없을 때만 Connect-RPC 세션 확보
        // INITIAL_SESSION: 페이지/탭 최초 로딩 시 localStorage 복원 경로
        // SIGNED_IN: 명시적 로그인 직후
        // TOKEN_REFRESHED: 토큰 갱신 후 session_id 유실 복구
        if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
          ensureConnectSession(`auth:${event}`, session.user?.id);
        }

        // 로그아웃 이벤트 처리 - Connect-RPC 세션 정리 (저장분 포함 — 명시
        // 로그아웃은 세션을 이어받을 의도가 없다는 뜻)
        if (event === 'SIGNED_OUT') {
          console.log('[AuthStore] User signed out, clearing Connect-RPC session');
          closeSession();
          useSessionStore.getState().clearSessionId();
          clearStoredSessionId(prevUserId);
          // 이슈 첨부 blob URL 캐시 회수 — 명시 로그아웃뿐 아니라 refresh
          // 실패 등 모든 SIGNED_OUT 경로에서: 같은 탭 계정 전환 시 이전
          // 사용자의 첨부가 인가 검사 없이 보이지 않게 (리뷰 반영)
          clearAttachmentUrlCache();
          // auth 위에 얹힌 스토어(편집기 userId 등)의 정리 — 등록부 경유(signOutHooks). 편집기 스토어를 여기서
          // 직접 가져오면 진입 청크에 도메인이 실린다(트랙 A-4)
          runSignOutHooks();
        }

        // 토큰 갱신 이벤트
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthStore] Token refreshed');
        }
      });

      _authStateUnsubscribe = () => {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.warn('[AuthStore] Failed to unsubscribe auth listener:', error);
        }
      };
    } catch (err) {
      console.error('[AuthStore] Initialization error:', err);
      set({
        user: null,
        session: null,
        loading: false,
        initialized: true,
        error: 'Failed to initialize authentication',
      });
    } finally {
      _initializePromise = null;
    }
    })();

    return _initializePromise;
  },

  /**
   * 이메일/비밀번호 로그인
   * B 정책: 이메일 로그인은 관리자 승인 필요
   */
  signInWithEmail: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null });

      const backend = await getBackend();

      // 1. 인증
      const { user, session } = await backend.auth.signInWithPassword(email, password);

      // 2. 프로필 조회 (승인 상태 확인은 ProtectedRoute에서 처리)
      // 조회 실패 시에도 로그인은 허용하고 ProtectedRoute가 pending/rejected 처리
      const profile = await backend.profiles.getProfile(user.id);

      // 3. 로그인 허용 - pending/rejected는 ProtectedRoute에서 차단
      set({
        user,
        session,
        profile,
        loading: false,
      });

      // 4. Connect-RPC 세션은 onAuthStateChange SIGNED_IN 이벤트에서 생성됨
      console.log('[AuthStore] Email sign in successful');
    } catch (err) {
      const authError = err as Error;
      console.error('[AuthStore] Email sign in error:', authError.message);
      set({
        loading: false,
        error: authError.message || '로그인에 실패했습니다.',
      });
      throw err;
    }
  },

  /**
   * 이메일/비밀번호 회원가입
   */
  signUpWithEmail: async (email: string, password: string, role?: 'engineer' | 'operator') => {
    try {
      set({ loading: true, error: null });

      const backend = await getBackend();
      await backend.auth.signUp(email, password, role);

      // 가입은 로그인이 아니다 — 관리자 승인 전까지 세션이 없고(backend 가 session: null 을 준다),
      // 여기서 user 를 채우면 보호 라우트가 통과해 «로그인 페이지로 이동» 이 승인 대기 화면으로
      // 간다(로컬 실측 2026-10-12). 완료 화면은 SignupPage 가 제 상태(signupSuccess)로 그린다.
      set({ loading: false });

      console.log('[AuthStore] Sign up successful');
    } catch (err) {
      const authError = err as Error;
      console.error('[AuthStore] Sign up error:', authError.message);
      set({
        loading: false,
        error: authError.message || '회원가입에 실패했습니다.',
      });
      throw err;
    }
  },

  /**
   * 로그아웃
   */
  signOut: async () => {
    const signedOutUserId = get().user?.id;
    try {
      set({ loading: true, error: null });

      const backend = await getBackend();
      await backend.auth.signOut();

      set({
        user: null,
        session: null,
        profile: null,
        loading: false,
      });

      // Connect-RPC 세션 스트림 종료 → 서버 측 세션 cleanup 트리거.
      // 저장분도 정리 — 명시 로그아웃은 세션을 이어받을 의도가 없다 (#286)
      closeSession();
      useSessionStore.getState().clearSessionId();
      clearStoredSessionId(signedOutUserId);

      console.log('[AuthStore] Sign out successful');
    } catch (err) {
      const authError = err as Error;
      console.error('[AuthStore] Sign out error:', authError.message);

      // 에러가 나도 로컬 state는 클리어
      set({
        user: null,
        session: null,
        profile: null,
        loading: false,
        error: null, // 세션 없음은 에러로 표시하지 않음
      });

      // Connect-RPC 세션 스트림 종료 → 서버 측 세션 cleanup 트리거
      closeSession();
      useSessionStore.getState().clearSessionId();
      clearStoredSessionId(signedOutUserId);
    }
  },

  /**
   * 에러 클리어
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * 로딩 상태 리셋 (OAuth 취소 등의 경우)
   */
  resetLoading: () => {
    set({ loading: false });
  },

  /**
   * 현재 사용자의 프로필 조회
   */
  fetchProfile: async () => {
    const user = get().user;
    if (!user) {
      set({ profile: null });
      return;
    }

    try {
      const backend = await getBackend();
      const profile = await backend.profiles.getProfile(user.id);

      // 프로필이 없으면 기본값 설정 (조회 거부·트리거 실패 등의 경우)
      if (!profile) {
        console.warn('[AuthStore] Profile not found, using default');
        set({ profile: buildPendingProfile(user) });
        return;
      }

      set({ profile });
    } catch (err) {
      console.error('[AuthStore] Profile fetch error:', err);
      // 에러 시에도 기본 프로필로 pending 상태 표시
      set({ profile: buildPendingProfile(get().user) });
    }
  },

  /**
   * 비밀번호 변경
   * 현재 비밀번호 검증 후 새 비밀번호로 변경
   */
  updatePassword: async (currentPassword: string, newPassword: string) => {
    const user = get().user;
    if (!user?.email) throw new Error('사용자 정보를 찾을 수 없습니다');

    const backend = await getBackend();
    await backend.auth.changePassword(currentPassword, newPassword);
  },

  /**
   * 표시 이름 변경
   */
  updateDisplayName: async (displayName: string) => {
    const user = get().user;
    if (!user) throw new Error('사용자 정보를 찾을 수 없습니다');

    const backend = await getBackend();
    await backend.profiles.updateDisplayName(user.id, displayName);

    // 프로필 새로고침
    await get().fetchProfile();
  },

  /**
   * [관리자] 모든 사용자 프로필 조회
   */
  fetchAllUsers: async (): Promise<UserProfile[]> => {
    try {
      const backend = await getBackend();
      return await backend.profiles.listAllUsers();
    } catch (err) {
      console.error('[AuthStore] Fetch users error:', err);
      throw err;
    }
  },

  /**
   * [관리자] 사용자 승인
   */
  approveUser: async (userId: string) => {
    try {
      const backend = await getBackend();
      await backend.profiles.approveUser(userId);
      console.log('[AuthStore] User approved:', userId);
    } catch (err) {
      console.error('[AuthStore] Approve user error:', err);
      throw err;
    }
  },

  /**
   * [관리자] 사용자 거부
   */
  rejectUser: async (userId: string) => {
    try {
      const backend = await getBackend();
      await backend.profiles.rejectUser(userId);
      console.log('[AuthStore] User rejected:', userId);
    } catch (err) {
      console.error('[AuthStore] Reject user error:', err);
      throw err;
    }
  },
}));
